// Copyright (c) Ping. All rights reserved.

import { useCallback, useEffect, useState } from 'react';
import { log } from '../../log/log';
import { sortEntries } from './sort';
import type {
  LeaderboardBoard,
  LeaderboardColumn,
  LeaderboardColumnId,
  LeaderboardEntry,
  LeaderboardId,
  LeaderboardLoader,
  LeaderboardSort,
  LeaderboardSortDirection,
  LeaderboardState,
  LeaderboardSubscriber,
  LeaderboardTemplate,
  LeaderboardTemplateLoader,
} from './types';

export interface UseLeaderboardViewModelArgs {
  readonly initialBoardId: LeaderboardId | null;
  readonly loadBoard: LeaderboardLoader;
  readonly subscribeBoard: LeaderboardSubscriber;
  readonly loadTemplates: LeaderboardTemplateLoader;
}

export interface LeaderboardViewModel {
  readonly state: LeaderboardState;
  readonly templates: readonly LeaderboardTemplate[];
  readonly selectedTemplateId: string | null;
  readonly sort: LeaderboardSort | null;
  readonly selectBoard: (boardId: LeaderboardId) => void;
  readonly selectTemplate: (templateId: string | null) => void;
  readonly setSort: (columnId: LeaderboardColumnId) => void;
  readonly addColumn: (column: LeaderboardColumn) => void;
  readonly removeColumn: (columnId: LeaderboardColumnId) => void;
  readonly addEntry: (entry: LeaderboardEntry) => void;
  readonly retry: () => void;
}

/**
 * Owns the Leaderboard state machine. The host wires the persistence
 * + transport layer; this hook handles the local in-memory derivations
 * (active board, sort, templates, picker selection).
 *
 * Cell values are sensitive — the view-model logs `{ verb, columnCount,
 * entryCount, kind }` shape only, never the values themselves.
 */
export function useLeaderboardViewModel(args: UseLeaderboardViewModelArgs): LeaderboardViewModel {
  const { loadBoard, subscribeBoard, loadTemplates } = args;
  const [boardId, setBoardId] = useState<LeaderboardId | null>(args.initialBoardId);
  const [board, setBoard] = useState<LeaderboardBoard | null>(null);
  const [templates, setTemplates] = useState<readonly LeaderboardTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [sort, setSortState] = useState<LeaderboardSort | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void retryNonce;

    const bootstrap = async (): Promise<void> => {
      setLoading(true);
      try {
        log.debug('leaderboard.bootstrap', { hasBoard: boardId !== null });
        const loadedTemplates = await loadTemplates();
        if (cancelled) {
          return;
        }
        setTemplates(loadedTemplates);
      } catch (e) {
        if (cancelled) {
          return;
        }
        log.error('leaderboard.templates.failed', e as Error);
        setError(e as Error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
    };
  }, [loadTemplates, boardId, retryNonce]);

  useEffect(() => {
    if (boardId === null) {
      setBoard(null);
      return;
    }
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    const apply = (loaded: LeaderboardBoard): void => {
      if (cancelled) {
        return;
      }
      setBoard(loaded);
    };

    setLoading(true);
    setError(null);
    void retryNonce;

    const bootstrap = async (): Promise<void> => {
      try {
        log.debug('leaderboard.board.load');
        unsubscribe = subscribeBoard(boardId, apply);
        const initial = await loadBoard(boardId);
        apply(initial);
      } catch (e) {
        if (cancelled) {
          return;
        }
        log.error('leaderboard.board.failed', e as Error);
        setError(e as Error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
      if (unsubscribe !== null) {
        unsubscribe();
      }
    };
  }, [boardId, loadBoard, subscribeBoard, retryNonce]);

  const selectBoard = useCallback((next: LeaderboardId) => {
    setBoardId(next);
    setSortState(null);
  }, []);

  const selectTemplate = useCallback((templateId: string | null) => {
    setSelectedTemplateId(templateId);
  }, []);

  const setSort = useCallback((columnId: LeaderboardColumnId) => {
    setSortState((current) => {
      if (current === null || current.columnId !== columnId) {
        return { columnId, direction: 'ascending' as LeaderboardSortDirection };
      }
      return {
        columnId,
        direction: current.direction === 'ascending' ? 'descending' : 'ascending',
      };
    });
  }, []);

  const addColumn = useCallback((column: LeaderboardColumn) => {
    setBoard((current) => {
      if (current === null) {
        return current;
      }
      return { ...current, columns: [...current.columns, column] };
    });
    log.debug('leaderboard.column.add');
  }, []);

  const removeColumn = useCallback((columnId: LeaderboardColumnId) => {
    setBoard((current) => {
      if (current === null) {
        return current;
      }
      const nextColumns = current.columns.filter((c) => c.id !== columnId);
      const nextEntries = current.entries.map((entry) => {
        if (!entry.values.has(columnId)) {
          return entry;
        }
        const nextValues = new Map(entry.values);
        nextValues.delete(columnId);
        return { ...entry, values: nextValues };
      });
      return { ...current, columns: nextColumns, entries: nextEntries };
    });
    setSortState((current) => {
      if (current === null || current.columnId !== columnId) {
        return current;
      }
      return null;
    });
    log.debug('leaderboard.column.remove');
  }, []);

  const addEntry = useCallback((entry: LeaderboardEntry) => {
    setBoard((current) => {
      if (current === null) {
        return current;
      }
      return { ...current, entries: [...current.entries, entry] };
    });
    log.debug('leaderboard.entry.add');
  }, []);

  const retry = useCallback(() => {
    setRetryNonce((n) => n + 1);
  }, []);

  const state: LeaderboardState = (() => {
    if (loading && board === null) {
      return { kind: 'loading' };
    }
    if (error !== null) {
      return { kind: 'error', error };
    }
    if (board === null) {
      return { kind: 'empty' };
    }
    return {
      kind: 'populated',
      board,
      entries: sortEntries(board.entries, sort),
      sort,
    };
  })();

  return {
    state,
    templates,
    selectedTemplateId,
    sort,
    selectBoard,
    selectTemplate,
    setSort,
    addColumn,
    removeColumn,
    addEntry,
    retry,
  };
}
